import { enqueueSnackbar } from "notistack";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { dueAutoptoOffer } from "~/components/autopto/isAutoptoTrial";
import { useTeam } from "~/contexts/TeamProvider";
import { useTeamPolling } from "~/hooks/useTeamPolling";
import { routes } from "~/router";

/**
 * Watches a blocked subscription until the access is restored.
 *
 * Adding a card or paying the open invoice unblocks the team through Stripe
 * webhooks, and the status doesn't always change (a card alone can be
 * enough), so the SubscriptionsChannel may stay silent: after such an
 * action the team is polled for a while. The invoice is paid on Stripe's
 * hosted page in another tab, so the team is also rechecked on focus.
 */
export function useBlockedAccessRecovery() {
  const { teamId, team, refetchTeam } = useTeam();
  const navigate = useNavigate();

  const blockedReason = team?.subscription?.blocked_reason ?? null;

  const { isPolling, startPolling, stopPolling } =
    useTeamPolling(!!blockedReason);
  const previous = useRef({ teamId, blockedReason });

  useEffect(() => {
    if (!blockedReason) return;

    const handleFocus = () => void refetchTeam();
    // Mobile browsers don't always fire focus when the tab comes back.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void refetchTeam();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [blockedReason, refetchTeam]);

  useEffect(() => {
    const { teamId: previousTeamId, blockedReason: wasBlocked } =
      previous.current;
    previous.current = { teamId, blockedReason };

    // Switching teams (the new one still loading) isn't a recovery.
    if (teamId !== previousTeamId || !team) {
      stopPolling();

      return;
    }

    // Also no change when the team just loaded already blocked: the billing
    // page may have started polling right then.
    if (blockedReason === wasBlocked || !wasBlocked) return;

    // Unblocked, or a saved card only turned "no card" into "payment
    // failed" (the next step needs the user): stop polling either way.
    stopPolling();

    if (!blockedReason) {
      if (wasBlocked === "trial_ended_no_card" && dueAutoptoOffer(team)) {
        navigate(routes.autoptoOffer(), { replace: true });
      } else {
        enqueueSnackbar("Your access has been restored.", {
          variant: "success",
        });
        navigate(routes.home());
      }
    }
  }, [teamId, team, blockedReason, navigate, stopPolling]);

  return { blockedReason, isPolling, startPolling };
}
